// Firestore service for Tax Categories management (Pure Taxonomy per PRD)
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION_NAME = 'taxCategories';

export const taxCategoriesService = {
  // Get all tax categories with real-time updates
  subscribeToCategories: (callback) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('isActive', '!=', false),
      orderBy('level'),
      orderBy('sortOrder')
    );
    
    return onSnapshot(q, (snapshot) => {
      const categories = [];
      snapshot.forEach((doc) => {
        categories.push({
          id: doc.id,
          ...doc.data()
        });
      });
      callback(categories);
    });
  },

  // Get all categories as a flat array
  getAll: async () => {
    const snapshot = await getDocs(
      query(
        collection(db, COLLECTION_NAME),
        where('isActive', '!=', false),
        orderBy('level'),
        orderBy('sortOrder')
      )
    );
    
    const categories = [];
    snapshot.forEach((doc) => {
      categories.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return categories;
  },

  // Get all categories including archived ones
  getAllIncludingArchived: async () => {
    const snapshot = await getDocs(
      query(
        collection(db, COLLECTION_NAME),
        orderBy('level'),
        orderBy('sortOrder')
      )
    );
    
    const categories = [];
    snapshot.forEach((doc) => {
      categories.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return categories;
  },

  // Get single category
  getCategory: async (categoryId) => {
    const docRef = doc(db, COLLECTION_NAME, categoryId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    }
    return null;
  },

  // Create new tax category (pure taxonomy structure per PRD)
  createCategory: async (categoryData) => {
    const categoryId = categoryData.id || `CAT_${Date.now()}`;
    const docRef = doc(db, COLLECTION_NAME, categoryId);
    
    const newCategory = {
      // Core taxonomy fields
      name: categoryData.name,
      pathString: categoryData.pathString,
      parentCategoryId: categoryData.parentCategoryId || null,
      description: categoryData.description,
      keywords: categoryData.keywords || [],
      isActive: categoryData.isActive !== false,
      level: categoryData.level || 0,
      sortOrder: categoryData.sortOrder || 1,
      
      // Regulatory attributes (per PRD)
      ageRestriction: categoryData.ageRestriction || 'none',
      licensingRequirements: categoryData.licensingRequirements || [],
      regulatoryFlags: categoryData.regulatoryFlags || [],
      
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(docRef, newCategory);
    return { id: categoryId, ...newCategory };
  },

  // Update existing category
  updateCategory: async (categoryId, updateData) => {
    const docRef = doc(db, COLLECTION_NAME, categoryId);
    
    const updates = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(docRef, updates);
    return updates;
  },

  // Archive category (soft delete)
  archiveCategory: async (categoryId) => {
    const docRef = doc(db, COLLECTION_NAME, categoryId);
    
    await updateDoc(docRef, {
      isActive: false,
      archivedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  },

  // Restore archived category
  restoreCategory: async (categoryId) => {
    const docRef = doc(db, COLLECTION_NAME, categoryId);
    
    await updateDoc(docRef, {
      isActive: true,
      archivedAt: null,
      updatedAt: serverTimestamp()
    });
  },

  // Delete category permanently
  deleteCategory: async (categoryId) => {
    const docRef = doc(db, COLLECTION_NAME, categoryId);
    await deleteDoc(docRef);
  },

  // Get category hierarchy (children of a parent)
  getCategoryChildren: async (parentId) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('parentCategoryId', '==', parentId),
      orderBy('sortOrder')
    );
    
    const snapshot = await getDocs(q);
    const children = [];
    snapshot.forEach((doc) => {
      children.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return children;
  },

  // Update category sort order
  updateSortOrder: async (updates) => {
    const batch = writeBatch(db);
    
    updates.forEach(({ categoryId, sortOrder }) => {
      const docRef = doc(db, COLLECTION_NAME, categoryId);
      batch.update(docRef, { 
        sortOrder, 
        updatedAt: serverTimestamp() 
      });
    });
    
    await batch.commit();
  },

  // Search categories by name or description
  searchCategories: async (searchTerm) => {
    // Note: Firestore doesn't support full-text search natively
    // This is a simple implementation - consider using Algolia for production
    const snapshot = await getDocs(collection(db, COLLECTION_NAME));
    const categories = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      const searchString = `${data.name} ${data.description} ${data.pathString}`.toLowerCase();
      
      if (searchString.includes(searchTerm.toLowerCase())) {
        categories.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return categories;
  },

  // Validate category hierarchy (prevent circular references)
  validateHierarchy: async (categoryId, newParentId) => {
    if (!newParentId) return true; // Root category is always valid
    
    // Check if new parent is a descendant of the category
    const isDescendant = await checkIfDescendant(categoryId, newParentId);
    return !isDescendant;
  },

  // Build hierarchical tree structure from flat array
  buildHierarchy: (categories) => {
    const categoryMap = new Map();
    const rootCategories = [];
    
    // Create a map for quick lookup
    categories.forEach(category => {
      categoryMap.set(category.id, { ...category, children: [] });
    });
    
    // Build hierarchy
    categories.forEach(category => {
      const categoryNode = categoryMap.get(category.id);
      
      if (category.parentCategoryId) {
        const parent = categoryMap.get(category.parentCategoryId);
        if (parent) {
          parent.children.push(categoryNode);
        }
      } else {
        rootCategories.push(categoryNode);
      }
    });
    
    return rootCategories;
  },

  // Flatten hierarchical structure to array
  flattenHierarchy: (categories) => {
    const result = [];
    
    function flatten(cats) {
      cats.forEach(category => {
        result.push(category);
        if (category.children && category.children.length > 0) {
          flatten(category.children);
        }
      });
    }
    
    flatten(categories);
    return result;
  }
};

// Helper function to check if a category is a descendant
async function checkIfDescendant(ancestorId, categoryId) {
  if (ancestorId === categoryId) return true;
  
  const category = await taxCategoriesService.getCategory(categoryId);
  if (!category || !category.parentCategoryId) return false;
  
  return await checkIfDescendant(ancestorId, category.parentCategoryId);
}

export default taxCategoriesService;